import jsPDF from "jspdf";
import domtoimage from "dom-to-image-more";

class Certificate {
  constructor() {
    this.sheetWidthPx = 1056;
    this.sheetHeightPx = 816;
    this.resizeObserver = null;
    this.handleWindowResize = this.updatePreviewScales.bind(this);
  }

  async inlineSpecialDistinctionSvgs(container) {
    const svgImgs = Array.from(
      container.querySelectorAll("img.special-distinction-star[src*='.svg']"),
    );

    const originals = [];

    await Promise.all(
      svgImgs.map(async (img) => {
        const originalSrc = img.getAttribute("src");
        if (!originalSrc) return;

        originals.push([img, originalSrc]);

        try {
          const absoluteUrl = new URL(originalSrc, window.location.origin).toString();
          const response = await fetch(absoluteUrl, { credentials: "same-origin" });
          if (!response.ok) return;
          const svgText = await response.text();
          const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
          img.setAttribute("src", dataUrl);
        } catch (error) {
          console.warn("Could not inline SVG for PDF capture:", originalSrc, error);
        }
      }),
    );

    return () => {
      originals.forEach(([img, src]) => {
        img.setAttribute("src", src);
      });
    };
  }

  init() {
    console.log("Certificate.init()");
    const rasterButtons = document.querySelectorAll("[data-action=generate-pdf]");
    const printButtons = document.querySelectorAll("[data-action=print-pdf]");
    console.log("Found raster buttons:", rasterButtons.length);
    console.log("Found print buttons:", printButtons.length);

    rasterButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        this.generatePDF(event);
      });
    });

    printButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        this.printPDF();
      });
    });

    this.setupPreviewScaling();
  }

  setupPreviewScaling() {
    const viewports = document.querySelectorAll("[data-certificate-preview-viewport]");
    if (!viewports.length) {
      return;
    }

    this.updatePreviewScales();

    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => {
        this.updatePreviewScales();
      });

      viewports.forEach((viewport) => {
        this.resizeObserver.observe(viewport);
      });
    } else {
      window.addEventListener("resize", this.handleWindowResize);
    }
  }

  updatePreviewScales() {
    const viewports = document.querySelectorAll("[data-certificate-preview-viewport]");
    if (!viewports.length) {
      return;
    }

    viewports.forEach((viewport) => {
      const viewportWidth = viewport.clientWidth;
      if (!viewportWidth) {
        return;
      }

      const scale = Math.min(viewportWidth / this.sheetWidthPx, 1);
      const scaledWidth = this.sheetWidthPx * scale;
      const offsetX = Math.max((viewportWidth - scaledWidth) / 2, 0);
      const scaledHeight = this.sheetHeightPx * scale;

      viewport.style.setProperty("--certificate-preview-scale", String(scale));
      viewport.style.setProperty("--certificate-preview-offset-x", `${offsetX}px`);
      viewport.style.setProperty("--certificate-preview-height", `${scaledHeight}px`);
    });
  }

  printPDF() {
    window.print();
  }

  async generatePDF() {
    // Check if jsPDF is available
    if (typeof jsPDF === "undefined") {
      console.error("jsPDF is not available");
      alert("PDF generation is not available. Please try again later.");
      return;
    }

    // Get the certificate element
    const certificate = document.querySelector("[data-certificate-slug]");
    if (!certificate) {
      console.error("Certificate element not found");
      return;
    }

    // Get the slug from the data attribute
    const certificateSlug = certificate.dataset.certificateSlug;
    console.log("Certificate slug:", certificateSlug);

    if (!certificateSlug) {
      console.error("Certificate slug not found");
      return;
    }

    try {
      // Create PDF with compression
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: [800, 600],
        compress: true,
      });

      const target = certificate;

      // Hide border during capture so it doesn't appear in the PDF
      const originalBorder = certificate.style.border;
      certificate.style.border = "none";
      const restoreSvgSources = await this.inlineSpecialDistinctionSvgs(certificate);

      try {
        const pngDataUrl = await domtoimage.toPng(target, {
          cacheBust: true,
          bgcolor: "transparent",
        });

        const pdfWidth = doc.internal.pageSize.getWidth();
        const pdfHeight = doc.internal.pageSize.getHeight();
        doc.addImage(pngDataUrl, "PNG", 0, 0, pdfWidth, pdfHeight);
        doc.save(`certificate-${certificateSlug}.pdf`);
      } catch (error) {
        console.error("Error generating certificate image:", error);
        alert("Error generating PDF. Please try again.");
      } finally {
        restoreSvgSources();
        certificate.style.border = originalBorder || "1px solid rgba(0, 0, 0, 0.2)";
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    }
  }
}
export default new Certificate();
